import {
  IconDeviceMobile,
  IconRefresh,
  IconSignal2g,
  IconAlertCircle,
  IconCpu,
} from "@tabler/icons-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { createFileRoute } from "@tanstack/react-router";
import { formatDistanceToNow } from "date-fns";
import { useAgentDevices, useAgentDeviceStats } from "@/hooks/use-agent-devices";
import { AppShell } from "@/components/layout/app-shell";
import { ProtectedRoute } from "@/components/layout/protected-route";

export const Route = createFileRoute("/devices/")({
  component: AgentDevicesPage,
});

function AgentDevicesPage() {
  return (
    <ProtectedRoute>
      <AppShell>
        <AgentDevicesContent />
      </AppShell>
    </ProtectedRoute>
  );
}

function AgentDevicesContent() {
  const { data: devices, isLoading, refetch } = useAgentDevices();
  const { data: stats, isLoading: statsLoading } = useAgentDeviceStats();

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case "online":
        return "default";
      case "offline":
        return "secondary";
      case "error":
        return "destructive";
      default:
        return "outline";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "online":
        return <IconSignal2g className="w-4 h-4" />;
      case "offline":
        return <IconAlertCircle className="w-4 h-4" />;
      case "error":
        return <IconAlertCircle className="w-4 h-4" />;
      default:
        return null;
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">My Devices</h1>
          <p className="text-muted-foreground mt-1">
            Monitor your agent devices and their status
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => refetch()}
          disabled={isLoading}
        >
          <IconRefresh className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Device Stats Overview */}
      <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Total Devices</CardTitle>
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <div className="h-9 w-12 animate-pulse bg-muted rounded" />
            ) : (
              <div className="text-2xl font-bold">{stats?.total_devices || 0}</div>
            )}
            <p className="text-xs text-muted-foreground mt-1">Registered devices</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Online</CardTitle>
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <div className="h-9 w-12 animate-pulse bg-muted rounded" />
            ) : (
              <div className="text-2xl font-bold text-green-600">
                {stats?.online || 0}
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-1">Active now</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Offline</CardTitle>
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <div className="h-9 w-12 animate-pulse bg-muted rounded" />
            ) : (
              <div className="text-2xl font-bold text-gray-600">
                {stats?.offline || 0}
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-1">Disconnected</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Total SIMs</CardTitle>
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <div className="h-9 w-12 animate-pulse bg-muted rounded" />
            ) : (
              <div className="text-2xl font-bold">{stats?.total_sims || 0}</div>
            )}
            <p className="text-xs text-muted-foreground mt-1">Across devices</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Disabled</CardTitle>
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <div className="h-9 w-12 animate-pulse bg-muted rounded" />
            ) : (
              <div className="text-2xl font-bold text-orange-600">
                {stats?.disabled || 0}
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-1">Disabled devices</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Total Captures</CardTitle>
          </CardHeader>
          <CardContent>
            {statsLoading ? (
              <div className="h-9 w-12 animate-pulse bg-muted rounded" />
            ) : (
              <div className="text-2xl font-bold text-purple-600">
                {stats?.total_captures || 0}
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-1">SMS captured</p>
          </CardContent>
        </Card>
      </div>

      {/* Devices List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <IconDeviceMobile className="w-5 h-5" />
            Device List
          </CardTitle>
          <CardDescription>
            View all your registered devices and their current status
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
              <p className="text-muted-foreground">Loading devices...</p>
            </div>
          ) : !devices || devices.length === 0 ? (
            <div className="text-center py-8">
              <IconDeviceMobile className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">No devices registered</p>
              <p className="text-sm text-muted-foreground">
                Use the Agent Device App to register your first device
              </p>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {devices.map((device) => (
                <Card key={device.id} className="relative">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <CardTitle className="text-base">
                          {device.device_name}
                        </CardTitle>
                        <CardDescription className="font-mono text-xs">
                          {device.device_id}
                        </CardDescription>
                      </div>
                      <Badge variant={getStatusBadgeVariant(device.status)}>
                        <div className="flex items-center gap-1">
                          {getStatusIcon(device.status)}
                          <span className="capitalize">{device.status}</span>
                        </div>
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Last heartbeat</span>
                      <span className="font-medium">
                        {formatDistanceToNow(new Date(device.last_heartbeat), {
                          addSuffix: true,
                        })}
                      </span>
                    </div>

                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">SIMs</span>
                      <span className="font-medium">
                        {device.active_sims} / {device.total_sims} active
                      </span>
                    </div>

                    {/* Progress bar for SIMs */}
                    <div className="w-full bg-secondary rounded-full h-2">
                      <div
                        className="bg-primary h-2 rounded-full"
                        style={{
                          width: `${device.total_sims > 0 ? (device.active_sims / device.total_sims) * 100 : 0}%`,
                        }}
                      />
                    </div>

                    <div className="flex items-center gap-2 pt-2 border-t">
                      <IconCpu className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">
                        {device.total_sims} SIM{device.total_sims !== 1 ? "s" : ""} assigned
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Device Information */}
      <Card>
        <CardHeader>
          <CardTitle>Device Status Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-3">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-green-100 text-green-600 flex items-center justify-center">
                <IconSignal2g className="w-4 h-4" />
              </div>
              <div>
                <p className="font-medium">Online</p>
                <p className="text-sm text-muted-foreground">
                  Device is connected and actively processing SMS
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center">
                <IconAlertCircle className="w-4 h-4" />
              </div>
              <div>
                <p className="font-medium">Offline</p>
                <p className="text-sm text-muted-foreground">
                  Device is disconnected or not responding
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-red-100 text-red-600 flex items-center justify-center">
                <IconAlertCircle className="w-4 h-4" />
              </div>
              <div>
                <p className="font-medium">Error</p>
                <p className="text-sm text-muted-foreground">
                  Device has encountered an error
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
